/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// react-router components
import { useLocation, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Toolbar from "@mui/material/Toolbar";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarIconButton,
  navbarMobileMenu,
  navbarRow,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import { Divider, FormControlLabel, } from "@mui/material";
import MDSwitch from "components/MDSwitch";
import MDTypography from "components/MDTypography";
import {
  setMiniSidenav,
  setOpenConfigurator,
  setTransparentNavbar,
  useMaterialUIController,
} from "context";
import { toast } from "react-toastify";
import useAuthStore from "store/useAuthStore";
import useGlobalStore from "store/useGlobalStore";
import Swal from "sweetalert2";

function DashboardNavbar({ absolute, light, isMini, hasKPI }) {
  const { showKPI, setShowKPI } = useGlobalStore();
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);


  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);
  const { profile, logout, notificationsBadgeCount } = useAuthStore()
  const navigate = useNavigate();

  // Render the notifications menu
  // const renderMenu = () => (
  //   <Menu
  //     anchorEl={openMenu}
  //     anchorReference={null}
  //     anchorOrigin={{
  //       vertical: "bottom",
  //       horizontal: "left",
  //     }}
  //     open={Boolean(openMenu)}
  //     onClose={handleCloseMenu}
  //     sx={{ mt: 2 }}
  //   >
  //     <NotificationItem icon={<Icon>email</Icon>} title="Check new messages" />
  //     <NotificationItem icon={<Icon>podcasts</Icon>} title="Manage Podcast sessions" />
  //     <NotificationItem icon={<Icon>shopping_cart</Icon>} title="Payment successfully completed" />
  //   </Menu>
  // );

  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >

      {/* <hr style={{ margin: "8px 0", border: 0, borderTop: "1px solid #eee" }} /> */}
      {/* <MDBox px={2} py={1}>
        <Link to="/profile" style={{ textDecoration: "none", color: "inherit" }}>
          <Typography variant="body2" sx={{ cursor: "pointer", fontSize: "12px" }} onClick={handleCloseMenu}>
            Profile
          </Typography>
        </Link>
      </MDBox> */}
      {profile?.full_name && <MDBox px={2} display="flex" flexDirection="column" alignItems="flex-start">
        <MDTypography sx={{ fontSize: "15px" }}>
          {profile?.full_name}
        </MDTypography>
        <MDTypography variant="caption" fontWeight="light" color="text" sx={{ fontSize: "12px" }}>
          {profile?.role?.name}
        </MDTypography>
      </MDBox>}
      <Divider />
      <NotificationItem icon={<Icon>key</Icon>} title="Change Password" onClick={() => navigate("/change-password")} />
      <NotificationItem icon={<Icon>logout</Icon>} title="Logout" onClick={() => {
        handleCloseMenu();
        Swal.fire({
          title: "Are you sure?",
          text: "You will be logged out.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, logout"
        }).then((result) => {
          if (!result.isConfirmed) return;
          logout();
          toast.success("Logged out successfully");
        });
      }} />
    </Menu>
  );


  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
        </MDBox>
        {!isMini && (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            {hasKPI && (
              <MDBox pr={1}>
                <FormControlLabel
                  label="KPI"
                  control={
                    <MDSwitch
                      checked={showKPI}
                      color={showKPI ? "primary" : "error"}
                      onChange={() => setShowKPI(!showKPI)}
                    />
                  }
                />
              </MDBox>
            )}
            <MDBox color={light ? "white" : "inherit"}>
              <IconButton
                sx={navbarIconButton}
                size="small"
                disableRipple
                aria-haspopup="true"
                onClick={handleOpenMenu}
              >
                <Icon sx={iconsStyle}>account_circle</Icon>
              </IconButton>
              {renderMenu()}
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              {/* <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={{ ...navbarIconButton, mr: notificationsBadgeCount > 0 ? -1 : 0 }}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={() => navigate("/notifications")}
              >
                <MDBox position="relative" display="flex" alignItems="center" >
                  <Icon sx={iconsStyle}>notifications</Icon>
                  {notificationsBadgeCount > 0 && (
                    <MDBox
                      sx={{
                        position: "relative",
                        top: -10,
                        right: 2,
                        minWidth: 16,
                        height: 16,
                        bgcolor: "error.main",
                        color: "white",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "10px",
                        fontWeight: "bold",
                        zIndex: 1,
                      }}
                    >
                      <span style={{ color: "white" }}>{notificationsBadgeCount}</span>
                    </MDBox>
                  )}
                </MDBox>
              </IconButton> */}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
